import { useMemo } from 'react';
import { useAppSelector } from '@/hooks';
import { useScopeQueryData } from '@/hooks/censorship';
import {
  ENTITY_QUERY_PARAM,
  TABLE_PAGE_QUERY_PARAM,
  TABLE_PAGE_SIZE_QUERY_PARAM,
  TEXT_QUERY_PARAM,
  TEXT_QUERY_TYPE_PARAM,
} from './constants';
import type { QueryParamEntries, SearchFieldAndOptions } from './types';
import { bentoKatsuEntityToResultsDataEntity, filtersStateToQueryParamEntries } from './utils';

export const useSearchQuery = () => useAppSelector((state) => state.query);

export const useSearchFilterFields = (): SearchFieldAndOptions[] => {
  const { filterSections } = useSearchQuery();
  return useMemo(() => filterSections.flatMap(({ fields }) => fields), [filterSections]);
};

export const useEntityAndTextQueryParams = (): QueryParamEntries => {
  const { hasPermission: queryDataPerm } = useScopeQueryData();
  const { selectedEntity, matchData, pageSize, textQuery, textQueryType } = useSearchQuery();
  return useMemo<QueryParamEntries>(() => {
    const qp: QueryParamEntries = [];

    if (selectedEntity) {
      qp.push(
        [ENTITY_QUERY_PARAM, selectedEntity],
        [TABLE_PAGE_QUERY_PARAM, matchData[bentoKatsuEntityToResultsDataEntity(selectedEntity)].page.toString()]
      );
    }

    if (queryDataPerm) {
      // only include the page size and text query type parameters if we're authorized to view results tables.
      // both of these control select dropdowns and so keeping their value even without a selected entity (pageSize)
      // / text searching (textQueryType) is more intuitive to preserve the UI state.
      qp.push([TABLE_PAGE_SIZE_QUERY_PARAM, pageSize.toString()], [TEXT_QUERY_TYPE_PARAM, textQueryType]);
    }

    if (textQuery) {
      // Only include text query parameter if textQuery is set to a non-false value.
      qp.push([TEXT_QUERY_PARAM, textQuery]);
    }

    return qp;
  }, [selectedEntity, matchData, pageSize, queryDataPerm, textQuery, textQueryType]);
};

/**
 * Combines filters and other query params that relate to the search slice into a single query param object.
 */
export const useSearchQueryParams = (): QueryParamEntries => {
  const { filters } = useSearchQuery();
  const otherQueryParams = useEntityAndTextQueryParams();
  return useMemo(() => [...filtersStateToQueryParamEntries(filters), ...otherQueryParams], [filters, otherQueryParams]);
};

export const useSearchableFields = () => {
  /**
   * Hook which calculates a set of searchable fields (which share IDs with charts), which can be used, for example, to
   * choose whether to add a click event to a chart for the field.
   */
  const { filterSections } = useSearchQuery();
  return useMemo(
    () => new Set(filterSections.flatMap((section) => section.fields).map((field) => field.id)),
    [filterSections]
  );
};
